import React, {ComponentType, createContext, useContext, useEffect, useState} from 'react';
import {useControllerProps} from '../../../Widget/ControllerContext';
import {AddressWithContactModel} from '../../../../../domain/models/checkout/AddressWithContact.model';
import {FormInstanceData, useFormInstance} from '../../../Form/useFormInstance';
import {useMemberDetailsData} from '../../../MemberDetails/WithMemberDetailsData';
import {ADD_NEW_ADDRESS_ID} from '../../../constants';
import {updateMandatoryFields} from '../../../../../domain/utils/cashier.utils';
import {usePaymentsApi} from '../../../../../common/components/WithPaymentsApi/WithPaymentsApi';
import {ContactModel} from '../../../../../domain/models/checkout/Contact.model';
import {
  isAddressModelEmpty,
  isBillingEqualToShipping,
  isContactModelEmpty,
} from '../../../../../domain/utils/billingDetails.utils';
import {ApiAddressFragment, FullAddressContactDetailsFragment} from '../../../../../gql/graphql';
import {useFunctionResultObservation} from '@wix/function-result-observation';
import {FullNameValue, StepId} from '../../../../../types/checkoutApp.types';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {getBillingFormInitialState, getVatFromVatFormValues} from '../../../Form/BillingForm/BillingForm.utils';
import {AddressModel} from '../../../../../domain/models/checkout/Address.model';
import {getAddressFromAddressFormValues, getContactDetailsFromContactFormValues} from '../../../Form/form.utils';
import {SPECS} from '../../../../../common/constants';
import {validateEmailAddress} from '../../../Form/CustomerDetailsForm/CustomerDetailsForm.utils';

interface BillingFormData {
  billing: FormInstanceData;
  isFormValid: () => Promise<boolean>;
  initForm: (billingInfo?: AddressWithContactModel) => void;
  updateContactCountry: (country: string) => void;
  getDataForSubmit: () => {
    contactDetails?: FullAddressContactDetailsFragment;
    address?: ApiAddressFragment;
    email?: string;
  };
  contactCountry?: string;
  email: string;
  isEmailValid: boolean;
  setEmail: (email: string) => void;
}

export type BillingDataContextType = {
  billingFormData: BillingFormData;
  setBillingSameAsShipping: React.Dispatch<React.SetStateAction<boolean>>;
  billingSameAsShipping: boolean;
  isShippingValidForBilling: boolean;
};

export const BillingDataContext = createContext({} as BillingDataContextType);

export function withBillingData<T extends object>(Component: ComponentType<T>) {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  return function Wrapper(props: T) {
    const {
      paymentStore: {setCashierMandatoryFields, shouldRequirePayment},
      formsStore,
      stepsManagerStore: {activeStep},
      checkoutStore: {checkout, isShippingFlow},
    } = useControllerProps();
    const {experiments} = useExperiments();
    const {paymentsApi} = usePaymentsApi();
    const billingFormData = useBillingFormData();

    const {withObservation} = useFunctionResultObservation();
    const validateShippingAddressForBilling = withObservation(formsStore, 'validateShippingAddressForBilling');

    const {selectedAddressesService, editMode, selectedAddressesServiceId} = useMemberDetailsData();
    const [isShippingValidForBilling, setIsShippingValidForBilling] = useState<boolean>(true);

    const calculateBillingSameAsShippingState = (): boolean => {
      return (
        isShippingFlow &&
        (!checkout.billingInfo || isBillingEqualToShipping(checkout.billingInfo, checkout.shippingDestination))
      );
    };

    const [billingSameAsShipping, setBillingSameAsShipping] = useState<boolean>(calculateBillingSameAsShippingState);

    useEffect(
      () => {
        if (activeStep.stepId !== StepId.paymentAndPlaceOrder || !checkout.shippingDestination) {
          return;
        }

        async function validate() {
          if (shouldRequirePayment) {
            await updateMandatoryFields(
              paymentsApi,
              setCashierMandatoryFields,
              checkout.shippingDestination?.address?.country
            );
          }

          void validateShippingAddressForBilling(checkout.shippingDestination!).then((value) => {
            setIsShippingValidForBilling(value);
          });
        }

        void validate();
      },
      /* eslint-disable react-hooks/exhaustive-deps */ [activeStep.stepId]
    );

    const withCheckoutHackForReact18Tests = experiments.enabled(SPECS.WithCheckoutHackForReact18Tests);

    useEffect(
      () => {
        if (selectedAddressesServiceId === ADD_NEW_ADDRESS_ID) {
          return billingFormData.initForm(new AddressWithContactModel({addressesServiceId: ADD_NEW_ADDRESS_ID}));
        } else if (
          (withCheckoutHackForReact18Tests && selectedAddressesServiceId) ||
          (!withCheckoutHackForReact18Tests && /* istanbul ignore next */ editMode)
        ) {
          billingFormData.initForm(selectedAddressesService);
        }
      },
      /* eslint-disable react-hooks/exhaustive-deps */ [editMode, selectedAddressesServiceId]
    );

    useEffect(() => {
      if (selectedAddressesServiceId !== ADD_NEW_ADDRESS_ID && shouldRequirePayment) {
        void updateMandatoryFields(paymentsApi, setCashierMandatoryFields, selectedAddressesService?.address?.country);
      }
    }, [selectedAddressesServiceId]);

    return (
      <BillingDataContext.Provider
        value={{
          billingFormData,
          setBillingSameAsShipping,
          billingSameAsShipping,
          isShippingValidForBilling,
        }}>
        <Component {...props} />
      </BillingDataContext.Provider>
    );
  };
}

export function useBillingData() {
  const {billingFormData, setBillingSameAsShipping, billingSameAsShipping, isShippingValidForBilling} =
    useContext(BillingDataContext);

  return {
    billingFormData: billingFormData?.billing?.data,

    isFormValid: billingFormData?.isFormValid,
    initForm: billingFormData?.initForm,
    updateContactCountry: billingFormData?.updateContactCountry,
    getBillingFormDataForSubmit: () => billingFormData?.getDataForSubmit(),
    setEmail: billingFormData?.setEmail,

    contactCountry: billingFormData?.contactCountry,
    email: billingFormData?.email,
    isEmailValid: billingFormData?.isEmailValid,

    setBillingSameAsShipping,
    billingSameAsShipping,

    isShippingValidForBilling,
  };
}

/* eslint-disable sonarjs/cognitive-complexity */
function useBillingFormData(): BillingFormData {
  const {
    checkoutStore: {checkout},
    stepsManagerStore: {isLeanFlow},
  } = useControllerProps();

  const {experiments} = useExperiments();
  const shouldSplitBillingInfoPrefill = experiments.enabled(SPECS.ShouldSplitBillingInfoPrefill);

  let initialAddress;
  let initialContactDetails;

  if (shouldSplitBillingInfoPrefill) {
    const hasBillingInfoAddress = !isAddressModelEmpty(checkout.billingInfo?.address);
    const hasBillingInfoContact = !isContactModelEmpty(checkout.billingInfo?.contact);

    initialAddress = hasBillingInfoAddress ? checkout.billingInfo?.address : checkout.shippingDestination?.address;
    initialContactDetails = hasBillingInfoContact
      ? checkout.billingInfo?.contact
      : checkout.shippingDestination?.contact;
  } else {
    const hasBillingInfo = Boolean(checkout.billingInfo);

    initialAddress = hasBillingInfo ? checkout.billingInfo?.address : checkout.shippingDestination?.address;
    initialContactDetails = hasBillingInfo ? checkout.billingInfo?.contact : checkout.shippingDestination?.contact;
  }

  const [contactCountry, setContactCountry] = useState(initialAddress?.country);
  const [email, setEmail] = useState(checkout.buyerInfo?.email ?? '');
  const [isEmailValid, setIsEmailValid] = useState(true);

  const billingForm = useFormInstance(
    getBillingFormInitialState({
      contact: initialContactDetails,
      initialAddress,
      billingContact: checkout.billingInfo?.contact,
    })
  );

  const isFormValid = async () => {
    const isEmailValidForSubmit = !isLeanFlow || validateEmailAddress(email);
    setIsEmailValid(isEmailValidForSubmit);
    return (await billingForm.isValid()) && isEmailValidForSubmit;
  };

  const initForm = (billingInfo: AddressWithContactModel | undefined = checkout.billingInfo) => {
    const initialAddress = getAddressDetailsForForm(billingInfo);
    if (initialAddress?.country && shouldSplitBillingInfoPrefill) {
      setContactCountry(initialAddress.country);
    }

    billingForm.data.setFormValues(
      getBillingFormInitialState({
        contact: getContactDetailsForForm(billingInfo),
        billingContact: billingInfo?.contact,
        initialAddress,
      })
    );
  };

  const getContactDetailsForForm = (billingInfo: AddressWithContactModel | undefined): ContactModel | undefined => {
    if (billingInfo?.addressesServiceId === ADD_NEW_ADDRESS_ID) {
      return undefined;
    }

    if (shouldSplitBillingInfoPrefill) {
      const hasBillingInfoContact = !isContactModelEmpty(checkout.billingInfo?.contact);
      return hasBillingInfoContact ? billingInfo?.contact : checkout.shippingDestination?.contact;
    } else {
      return billingInfo ? billingInfo?.contact : checkout.shippingDestination?.contact;
    }
  };

  const getAddressDetailsForForm = (billingInfo: AddressWithContactModel | undefined): AddressModel | undefined => {
    if (shouldSplitBillingInfoPrefill) {
      if (billingInfo?.addressesServiceId === ADD_NEW_ADDRESS_ID) {
        return undefined;
      }

      const hasBillingInfoAddress = !isAddressModelEmpty(billingInfo?.address);
      return hasBillingInfoAddress ? billingInfo?.address : checkout.shippingDestination?.address;
    }

    return billingInfo ? billingInfo?.address : checkout.shippingDestination?.address;
  };

  const areFormsRendered = () => billingForm.isRendered();

  const updateContactCountry = (country: string) => {
    setContactCountry(country);
    billingForm.data.setFormValues({
      ...billingForm.data.formValues,
      full_name: {
        ...(billingForm.data.formValues.full_name as FullNameValue),
        country,
      },
    });
  };

  const getDataForSubmit = (): {
    contactDetails?: FullAddressContactDetailsFragment;
    address?: ApiAddressFragment;
    email?: string;
  } => {
    if (!areFormsRendered()) {
      return {};
    }

    const contactDetails = getContactDetailsFromContactFormValues(billingForm.data.formValues);
    const address = getAddressFromAddressFormValues(billingForm.data.formValues);
    const vatId = getVatFromVatFormValues(billingForm.data.formValues);

    return {
      contactDetails: {
        ...contactDetails,
        ...(vatId ? {vatId} : {}),
      },
      address,
      ...(isLeanFlow && {email}),
    };
  };

  return {
    billing: billingForm,
    isFormValid,
    initForm,
    updateContactCountry,
    getDataForSubmit,
    contactCountry,
    email,
    setEmail: (email: string) => {
      setEmail(email);
      setIsEmailValid(true);
    },
    isEmailValid,
  };
}
